import cx from "classnames";
import React, { useMemo, useState } from "react";

import { getArticleTitleAsString } from "@/mk2/apps/blogs/containers/Article/Article.helpers";
import { blogsPostDetailUrl } from "@/mk2/apps/blogs/urls";
import ReviewBlogPostPreview from "@/mk2/apps/wiki/components/ReviewBlogPostPreview";
import { getArticleBadges } from "@/mk2/components/Badge";
import { getAllDescendantsOfAType } from "@/mk2/helpers/article_ast/utils";
import { url } from "@/mk2/helpers/urls";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { BlogPostEntity } from "@/mk2/schemas";
import { FEED_DISPLAY_MORE } from "@/mk/autogenerated/translations/LinkedArticles.99cc089fd1778a1cb241094cc7c88511";
import { ACTIVE_SERVER_ID } from "@/mk/settings";

import styles from "./LinkedArticles.mscss";

interface OwnProps {
    className?: string;
    header: string;
    articles: BlogPostEntity[];
}

type Props = OwnProps;

const LIMIT = 3;

const HASHTAG = {
    201: "#redakcna_recenzia",
    202: "#redakcni_recenze",
}[ACTIVE_SERVER_ID];

function sortFn(a: BlogPostEntity, b: BlogPostEntity) {
    const aHashtags = getAllDescendantsOfAType(a.contentAST, "hashtag");
    const bHashtags = getAllDescendantsOfAType(b.contentAST, "hashtag");
    const aIsRedactionReview = aHashtags.find((hashtag) => hashtag.text === HASHTAG);
    const bIsRedactionReview = bHashtags.find((hashtag) => hashtag.text === HASHTAG);

    if (aIsRedactionReview && !bIsRedactionReview) {
        return -1;
    } else if (!aIsRedactionReview && bIsRedactionReview) {
        return 1;
    } else {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
}

interface ArticlesBadges {
    [id: number]: React.ReactNode[];
}

export const LinkedArticles: React.FunctionComponent<Props> = React.memo(
    ({ className, header, articles }) => {
        const [expanded, setExpanded] = useState<boolean>(false);

        const sortedObjects = [...articles].sort(sortFn);
        const objectsToRender = expanded ? sortedObjects : sortedObjects.slice(0, LIMIT);

        const showMore = articles.length > 0 && objectsToRender.length < articles.length;

        const onExpandClick = useEventCallback(() => {
            setExpanded(true);
        }, [setExpanded]);

        const articleBadges = useMemo((): ArticlesBadges => {
            const dict: ArticlesBadges = {};
            for (const blogPostArticle of articles) {
                dict[blogPostArticle.id] = getArticleBadges(
                    blogPostArticle.hashtagItems,
                ).renderedBadges;
            }
            return dict;
        }, [articles]);

        return (
            <div className={cx(styles.LinkedArticles, className)}>
                <h2 className={styles.LinkedArticles__headline}>{header}</h2>
                {objectsToRender.map((blogPostArticle, index) => {
                    const author = blogPostArticle.user;
                    const photo =
                        blogPostArticle.photos.length > 0 ? blogPostArticle.photos[0] : undefined;
                    const title = getArticleTitleAsString(blogPostArticle.titleAST);
                    const postUrl = author
                        ? url(blogsPostDetailUrl(blogPostArticle.type), {
                              username: author.username,
                              postSlug: blogPostArticle.slug,
                          })
                        : null;

                    return (
                        <ReviewBlogPostPreview
                            className={styles.LinkedArticles__article}
                            key={index}
                            photo={photo}
                            title={title}
                            postUrl={postUrl}
                            badges={articleBadges[blogPostArticle.id]}
                            commentsCount={blogPostArticle.commentsCount}
                            likersCount={blogPostArticle.likeable.likesCount}
                        />
                    );
                })}
                {showMore && (
                    <div>
                        <button className={styles.LinkedArticles__expand} onClick={onExpandClick}>
                            {FEED_DISPLAY_MORE} ({articles.length})
                        </button>
                    </div>
                )}
            </div>
        );
    },
);

LinkedArticles.displayName = "LinkedArticles";
